import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown, ChevronRight, Plus, Trash2, ArrowUp, ArrowDown, Pencil, Check, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete, apiPut } from '../../api/client';
import { Button } from '../../components/ui/Button';
import type { ChecklistTemplate, ChecklistTemplateItem, EventType } from '../../api/types';

const EVENT_TYPES: EventType[] = ['MATRIMONIO', 'BATTESIMO', 'COMUNIONE', 'CRESIMA', 'COMPLEANNO', 'RITRATTO', 'ALTRO'];
const EVENT_TYPE_LABELS: Record<EventType, string> = {
  MATRIMONIO: 'Matrimonio', BATTESIMO: 'Battesimo', COMUNIONE: 'Comunione',
  CRESIMA: 'Cresima', COMPLEANNO: 'Compleanno', RITRATTO: 'Ritratto', ALTRO: 'Altro',
};

const inputStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', width: '100%',
};

const selectStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text-muted)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.5rem', outline: 'none', cursor: 'pointer',
};

const iconBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-muted)', padding: '0.25rem',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};

export function ChecklistTemplatesSection() {
  const qc = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', eventType: '' as EventType | '', isDefault: false });
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', eventType: '' as EventType | '' });
  const [newItemLabel, setNewItemLabel] = useState<Record<string, string>>({});
  const [newItemCategory, setNewItemCategory] = useState<Record<string, string>>({});
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editItemForm, setEditItemForm] = useState({ label: '', category: '' });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['checklist-templates'] });

  const { data: templates = [], isLoading } = useQuery<ChecklistTemplate[]>({
    queryKey: ['checklist-templates'],
    queryFn: () => apiGet<ChecklistTemplate[]>('/checklist-templates'),
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; eventType?: EventType; isDefault?: boolean }) =>
      apiPost('/checklist-templates', data),
    onSuccess: () => { invalidate(); setShowNewForm(false); setNewForm({ name: '', eventType: '' }); toast.success('Template creato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; eventType?: EventType | null; isDefault?: boolean } }) =>
      apiPatch(`/checklist-templates/${id}`, data),
    onSuccess: () => { invalidate(); setEditingTemplateId(null); toast.success('Template aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/checklist-templates/${id}`),
    onSuccess: () => { invalidate(); toast.success('Template eliminato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const addItemMutation = useMutation({
    mutationFn: ({ templateId, label, category }: { templateId: string; label: string; category: string }) =>
      apiPost(`/checklist-templates/${templateId}/items`, { label, category: category || undefined }),
    onSuccess: (_data, vars) => { invalidate(); setNewItemLabel(p => ({ ...p, [vars.templateId]: '' })); toast.success('Item aggiunto'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ templateId, itemId, label, category }: { templateId: string; itemId: string; label: string; category: string }) =>
      apiPatch(`/checklist-templates/${templateId}/items/${itemId}`, { label, category: category || undefined }),
    onSuccess: () => { invalidate(); setEditingItemId(null); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteItemMutation = useMutation({
    mutationFn: ({ templateId, itemId }: { templateId: string; itemId: string }) =>
      apiDelete(`/checklist-templates/${templateId}/items/${itemId}`),
    onSuccess: () => { invalidate(); toast.success('Item eliminato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const reorderMutation = useMutation({
    mutationFn: ({ templateId, items }: { templateId: string; items: ChecklistTemplateItem[] }) =>
      apiPut(`/checklist-templates/${templateId}/items`, { items }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message),
  });

  const moveItem = (template: ChecklistTemplate, itemId: string, direction: 'up' | 'down') => {
    const items = [...template.items];
    const idx = items.findIndex(i => i.id === itemId);
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === items.length - 1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [items[idx], items[swapIdx]] = [items[swapIdx], items[idx]];
    const reordered = items.map((it, i) => ({ ...it, order: i }));
    reorderMutation.mutate({ templateId: template.id, items: reordered });
  };

  const startEdit = (t: ChecklistTemplate) => {
    setEditingTemplateId(t.id);
    setEditForm({ name: t.name, eventType: t.eventType ?? '', isDefault: t.isDefault });
  };

  const saveEdit = (id: string) => {
    updateMutation.mutate({
      id,
      data: {
        name: editForm.name,
        eventType: editForm.eventType ? (editForm.eventType as EventType) : null,
        isDefault: editForm.isDefault,
      },
    });
  };

  const CATEGORIES_TEMPLATE = ['Pre-evento', 'Giorno evento', 'Post-produzione', 'Consegna'];

  return (
    <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
      <div style={{
        padding: '0.875rem 1.25rem', borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>Template Checklist</h3>
        <Button size="sm" variant="secondary" icon={<Plus size={13} />} onClick={() => setShowNewForm(v => !v)}>
          Nuovo template
        </Button>
      </div>

      <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {showNewForm && (
          <div style={{
            border: '1px dashed rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
            padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem',
            background: 'var(--glass-bg-card)',
          }}>
            <p style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>Nuovo template</p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                placeholder="Nome template"
                value={newForm.name}
                onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
                style={inputStyle}
              />
              <select
                value={newForm.eventType}
                onChange={e => setNewForm(f => ({ ...f, eventType: e.target.value as EventType }))}
                style={selectStyle}
              >
                <option value="">Tutti i tipi</option>
                {EVENT_TYPES.map(et => <option key={et} value={et}>{EVENT_TYPE_LABELS[et]}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="ghost" onClick={() => setShowNewForm(false)}>Annulla</Button>
              <Button
                size="sm"
                loading={createMutation.isPending}
                disabled={!newForm.name.trim()}
                onClick={() => createMutation.mutate({
                  name: newForm.name.trim(),
                  eventType: newForm.eventType ? (newForm.eventType as EventType) : undefined,
                })}
              >
                Crea
              </Button>
            </div>
          </div>
        )}

        {isLoading && <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', padding: '0.5rem 0' }}>Caricamento…</p>}

        {!isLoading && templates.length === 0 && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', padding: '0.5rem 0' }}>
            Nessun template. Creane uno con il pulsante in alto.
          </p>
        )}

        {templates.map(template => {
          const isExpanded = expandedId === template.id;
          const isEditing = editingTemplateId === template.id;

          return (
            <div key={template.id} style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.625rem',
                padding: '0.625rem 0.875rem', background: 'var(--glass-bg-card)',
                cursor: isEditing ? 'default' : 'pointer',
              }}
                onClick={() => { if (!isEditing) setExpandedId(isExpanded ? null : template.id); }}
              >
                {isEditing ? (
                  <div style={{ display: 'flex', flex: 1, gap: '0.5rem', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                    <input
                      value={editForm.name}
                      onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                      style={{ ...inputStyle, flex: 1, fontSize: 'var(--text-sm)' }}
                      autoFocus
                    />
                    <select
                      value={editForm.eventType}
                      onChange={e => setEditForm(f => ({ ...f, eventType: e.target.value as EventType }))}
                      style={{ ...selectStyle, fontSize: 'var(--text-xs)' }}
                    >
                      <option value="">Tutti</option>
                      {EVENT_TYPES.map(et => <option key={et} value={et}>{EVENT_TYPE_LABELS[et]}</option>)}
                    </select>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={editForm.isDefault} onChange={e => setEditForm(f => ({ ...f, isDefault: e.target.checked }))} />
                      Predefinito
                    </label>
                    <button onClick={() => saveEdit(template.id)} style={iconBtnStyle} title="Salva"><Check size={14} color="var(--color-success)" /></button>
                    <button onClick={() => setEditingTemplateId(null)} style={iconBtnStyle} title="Annulla"><X size={14} color="var(--color-text-muted)" /></button>
                  </div>
                ) : (
                  <>
                    {isExpanded ? <ChevronDown size={14} color="var(--color-text-muted)" /> : <ChevronRight size={14} color="var(--color-text-muted)" />}
                    <span style={{ flex: 1, fontSize: 'var(--text-sm)', fontWeight: 500 }}>{template.name}</span>
                    {template.eventType && (
                      <span style={{ fontSize: 'var(--text-xs)', background: '#3E91FF', color: '#FFFFFF', padding: '0.125rem 0.5rem', borderRadius: 'var(--radius-full)' }}>
                        {EVENT_TYPE_LABELS[template.eventType]}
                      </span>
                    )}
                    {template.isDefault && (
                      <span style={{ fontSize: 'var(--text-xs)', background: '#22C55E', color: '#FFFFFF', padding: '0.125rem 0.5rem', borderRadius: 'var(--radius-full)' }}>
                        predefinito
                      </span>
                    )}
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{template.items.length} item</span>
                    <button onClick={e => { e.stopPropagation(); startEdit(template); }} style={iconBtnStyle} title="Modifica"><Pencil size={13} /></button>
                    <button
                      onClick={e => { e.stopPropagation(); if (confirm(`Eliminare il template "${template.name}"?`)) deleteMutation.mutate(template.id); }}
                      style={{ ...iconBtnStyle, color: 'var(--color-danger)' }}
                      title="Elimina"
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>

              {isExpanded && !isEditing && (
                <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {template.items.length === 0 && (
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', padding: '0.25rem 0' }}>Nessun item. Aggiungine uno qui sotto.</p>
                  )}

                  {template.items.map((item, idx) => {
                    const isEditingItem = editingItemId === item.id;
                    return (
                      <div key={item.id} style={{
                        display: 'flex', alignItems: 'center', gap: '0.375rem',
                        padding: '0.375rem 0.5rem', borderRadius: 'var(--radius-interactive)',
                        background: 'var(--glass-bg-card)',
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                          <button onClick={() => moveItem(template, item.id, 'up')} disabled={idx === 0} style={{ ...iconBtnStyle, padding: '1px', opacity: idx === 0 ? 0.3 : 1 }}><ArrowUp size={11} /></button>
                          <button onClick={() => moveItem(template, item.id, 'down')} disabled={idx === template.items.length - 1} style={{ ...iconBtnStyle, padding: '1px', opacity: idx === template.items.length - 1 ? 0.3 : 1 }}><ArrowDown size={11} /></button>
                        </div>

                        {isEditingItem ? (
                          <>
                            <input
                              value={editItemForm.label}
                              onChange={e => setEditItemForm(f => ({ ...f, label: e.target.value }))}
                              style={{ ...inputStyle, flex: 1, fontSize: 'var(--text-xs)', padding: '0.25rem 0.5rem' }}
                              autoFocus
                            />
                            <select
                              value={editItemForm.category}
                              onChange={e => setEditItemForm(f => ({ ...f, category: e.target.value }))}
                              style={{ ...selectStyle, fontSize: 'var(--text-xs)', padding: '0.25rem 0.5rem' }}
                            >
                              <option value="">Nessuna categoria</option>
                              {CATEGORIES_TEMPLATE.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <button onClick={() => updateItemMutation.mutate({ templateId: template.id, itemId: item.id, label: editItemForm.label, category: editItemForm.category })} style={iconBtnStyle}><Check size={13} color="var(--color-success)" /></button>
                            <button onClick={() => setEditingItemId(null)} style={iconBtnStyle}><X size={13} color="var(--color-text-muted)" /></button>
                          </>
                        ) : (
                          <>
                            <span style={{ flex: 1, fontSize: 'var(--text-xs)' }}>{item.label}</span>
                            {item.category && (
                              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', whiteSpace: 'nowrap' }}>{item.category}</span>
                            )}
                            <button onClick={() => { setEditingItemId(item.id); setEditItemForm({ label: item.label, category: item.category ?? '' }); }} style={iconBtnStyle}><Pencil size={11} /></button>
                            <button onClick={() => deleteItemMutation.mutate({ templateId: template.id, itemId: item.id })} style={{ ...iconBtnStyle, color: 'var(--color-danger)' }}><Trash2 size={11} /></button>
                          </>
                        )}
                      </div>
                    );
                  })}

                  <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.25rem' }}>
                    <input
                      placeholder="Nuovo item…"
                      value={newItemLabel[template.id] ?? ''}
                      onChange={e => setNewItemLabel(p => ({ ...p, [template.id]: e.target.value }))}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && (newItemLabel[template.id] ?? '').trim()) {
                          addItemMutation.mutate({ templateId: template.id, label: (newItemLabel[template.id] ?? '').trim(), category: newItemCategory[template.id] ?? '' });
                        }
                      }}
                      style={{ ...inputStyle, flex: 1, fontSize: 'var(--text-xs)', padding: '0.375rem 0.5rem' }}
                    />
                    <select
                      value={newItemCategory[template.id] ?? ''}
                      onChange={e => setNewItemCategory(p => ({ ...p, [template.id]: e.target.value }))}
                      style={{ ...selectStyle, fontSize: 'var(--text-xs)', padding: '0.375rem 0.5rem' }}
                    >
                      <option value="">Nessuna categoria</option>
                      {CATEGORIES_TEMPLATE.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <Button
                      size="sm"
                      icon={<Plus size={12} />}
                      loading={addItemMutation.isPending}
                      disabled={!(newItemLabel[template.id] ?? '').trim()}
                      onClick={() => addItemMutation.mutate({ templateId: template.id, label: (newItemLabel[template.id] ?? '').trim(), category: newItemCategory[template.id] ?? '' })}
                    >
                      Aggiungi
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
